import React, { useEffect, useMemo, useState } from "react";

import { ITeeTimeTabNew } from "../NewTeeSheet";
import { IDivision } from "redux/reducers/models/facility";
import { ITeeTimeSlotNew } from "redux/reducers/models/teetime";
import { convertTime } from "helpers/Helpers";
import { calculateDivisionColors } from "../NewTeeSheetTabs";
import "../newTeeSheetTabsDivision.scss";

import "./teeSheetOverview.scss";

export default function TeeSheetOverview(props: { teetimes: ITeeTimeTabNew[], selectTime: (time: string) => void, allDivisions: IDivision[] }) {

    const { teetimes, selectTime, allDivisions } = props;

    const [formattedTeeTimes, setFormattedTeeTimes] = useState<ITeeTimeTabNew[]>(null);

    useEffect(() => {
        if (teetimes) {
            formatTeeTimes(teetimes);
        }
    }, [teetimes]);

    // Sort the slots so that booked slots appear first
    const sortSlots = (a: ITeeTimeSlotNew, b: ITeeTimeSlotNew) => {
        const valA = a.tee_time_booking_id;
        const valB = b.tee_time_booking_id;

        if (valA === undefined && valB !== undefined) {
            return -1;
        }
        if (valA !== undefined && valB === undefined) {
            return 1;
        }
        if (valA === undefined && valB === undefined) {
            return 0;
        }

        if (valA === null && valB !== null) {
            return 1;
        }
        if (valA !== null && valB === null) {
            return -1;
        }
        if (valA === null && valB === null) {
            return 0;
        }

        return String(valA).localeCompare(String(valB));
    };

    function formatTeeTimes(teeTimes: ITeeTimeTabNew[]) {
        teeTimes.forEach((teetime, index) => {
            if (index % 2 === 1) {
                return;
            }

            teetime.slots = teetime.slots?.slice().sort(sortSlots);
            if (teeTimes[index+1]) {
                teeTimes[index+1].slots = teeTimes[index+1]?.slots?.slice().sort(sortSlots);
            }
        });

        setFormattedTeeTimes(teeTimes);
    }

    const groupedTeeTimes = useMemo(() => {

        if (!formattedTeeTimes) {
            return null;
        }

        const byDivision = formattedTeeTimes.reduce((acc: Record<string, ITeeTimeTabNew[]>, teeTime) => {
            const division_id = teeTime?.slots[0]?.division_id;
            if (!acc[division_id]) {
                acc[division_id] = [];
            }
            acc[division_id].push(teeTime);
            return acc;
        }, {});

        const finalGroupedData: Record<string, Record<string, ITeeTimeTabNew[]>> = {};

        for (const divisionId in byDivision) {
            const divisionTeeTimes = byDivision[divisionId];

            const uniqueTimes = [...new Set(divisionTeeTimes.map(t => t.start_time))];

            uniqueTimes.sort();

            const timeMap: Record<string, string> = {};
            for (let i = 0; i < uniqueTimes.length; i += 2) {
                const bucketKey = uniqueTimes[i];
                timeMap[bucketKey] = bucketKey;

                if (uniqueTimes[i + 1]) {
                const pairedTime = uniqueTimes[i + 1];
                timeMap[pairedTime] = bucketKey;
                }
            }

            const subGroups = divisionTeeTimes.reduce((acc: Record<string, ITeeTimeTabNew[]>, teeTime) => {
                const { start_time } = teeTime;
                const bucketKey = timeMap[start_time];

                if (!acc[bucketKey]) {
                    acc[bucketKey] = [];
                }
                acc[bucketKey].push(teeTime);
                return acc;
            }, {});

            finalGroupedData[divisionId] = subGroups;
        }

        return finalGroupedData;

    }, [formattedTeeTimes]);

    function getSlotColor(slot: ITeeTimeSlotNew) {
        if (slot.tee_time_booking_id) {
            if (slot.no_show_type !== null && slot.no_show_type !== "") {
                if (slot.no_show_type === "weather") {
                    return "#fcbf49";
                }
                return "#d62828";
            }
            if (slot.check_in_status === "checked") {
                return "#82d195";
            }
            if (slot.customer_type_color) {
                return slot.customer_type_color;
            }
            return "#3D5FA0";
        }

        if (slot.blocked_type !== null && slot.blocked_type !== "" && slot.blocked_type !== "open") {
            return "#e7e7e7";
        }
        return "white";
    }

    function getTeeTimeColor(slot: ITeeTimeSlotNew, isText: boolean = false) {
        if (slot.league_id || slot.tournament_id) {
            return isText ? "white" : "#475467";
        }
        return undefined;
    }
    
    return (
        <div className="jump-to-container">
            {groupedTeeTimes ? (
                <div className="jump-to-divisions">
                    {Object.entries(groupedTeeTimes)?.map(([division_id, divisionTeeTimes], index) => (
                        <div key={index} className="jump-to-times-container">
                            <div
                                style={calculateDivisionColors(
                                    allDivisions?.find((division: IDivision) => division.id === Number(division_id))?.color,
                                )}
                                className="mb-2 w-full text-center"
                            >
                                {allDivisions.find(division => division.id === Number(division_id))?.title}
                            </div>
                            {Object.entries(divisionTeeTimes)?.sort().map(([hour, teetimes], index) => (
                                <div
                                    className="jump-to-time"
                                    key={index}
                                    onClick={() => selectTime(teetimes[0].start_time)}
                                >
                                    <div className="jump-to-time-text" style={{ backgroundColor: getTeeTimeColor(teetimes[0].slots[0]), color: getTeeTimeColor(teetimes[0].slots[0], true) }}>{convertTime(hour, false, true)}</div>
                                    <div>
                                        <div className="flex">
                                            {teetimes[0].slots?.map((slot, slotIndex) => (
                                                <div key={slotIndex} className="slot-box-container" style={{ width: "7.5px", height: "7.5px", backgroundColor: getSlotColor(slot) }}></div>
                                            ))}
                                        </div>
                                        <div className="flex">
                                            {teetimes[1]?.slots?.map((slot, slotIndex) => (
                                                <div key={slotIndex} className="slot-box-container" style={{ width: "7.5px", height: "7.5px", backgroundColor: getSlotColor(slot) }}></div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : null }
        </div>
    );
}